/**
 * FormData request helpers for createBaseApiService
 * Extracted to reduce function size
 */

import {
	resolveAccessTokenForEndpoint,
} from "@Front/Api/helpers/authTokenResolutionHelpers";
import { LoggerService } from "@Front/Services/LoggerService/LoggerService";
import { fetchWithNetworkLogging, sendRequest } from "@Front/Api/helpers/requestHelpers";

/**
 * Builds headers for FormData request
 */
export function buildFormDataHeaders(endpoint?: string): Record<string, string> {
	const { token } = resolveAccessTokenForEndpoint(endpoint);
	const headers: Record<string, string> = {};
	if (token) {
		headers["Authorization"] = `Bearer ${token}`;
	} else {
		LoggerService.warn("⚠️ [BaseApiService] No access token cookie available for form-data request");
	}
	return headers;
}

/**
 * Logs FormData entries for debugging
 */
export function logFormDataEntries(body: FormData): Array<{ key: string; value: string | File }> {
	const formDataEntries: Array<{ key: string; value: string | File }> = [];
	for (const [key, value] of body.entries()) {
		if (value instanceof File) {
			formDataEntries.push({
				key,
				value: `File(${value.name}, ${value.size} bytes, ${value.type})`,
			});
		} else {
			formDataEntries.push({ key, value: String(value).substring(0, 100) });
		}
	}
	return formDataEntries;
}

/**
 * Creates POST FormData request
 */
export async function createPostFormDataRequest<T>(url: URL, body: FormData): Promise<T> {
	const headers = buildFormDataHeaders(url.pathname);
	const formDataEntries = logFormDataEntries(body);

	LoggerService.info("📤 [BaseApiService] Sending FormData request", {
		url: url.toString(),
		formDataEntries,
		headersCount: Object.keys(headers).length,
		hasAuth: !!headers["Authorization"],
	});

	const request = async (): Promise<Response> => {
		const response = await fetchWithNetworkLogging(
			url.toString(),
			{
				method: "POST",
				headers,
				body,
				credentials: "include",
			},
			{ timeoutProfile: "upload" },
		);

		const contentType = response.headers.get("Content-Type");
		LoggerService.info("📥 [BaseApiService] FormData response received", {
			status: response.status,
			statusText: response.statusText,
			contentType,
			url: response.url,
		});

		return response;
	};

	return sendRequest<T>(request);
}

/**
 * Normalizes file name by removing accents (NFD normalization)
 * @param fileName Original file name
 * @returns Normalized file name without accents
 */
export function normalizeFileName(fileName: string): string {
	return fileName.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

/**
 * Creates FormData with file(s) for upload
 * Backend expects field name "files" (plural) for all file upload endpoints
 * @param files File or array of files to upload
 * @param options Optional configuration
 * @param options.normalizeFileName Whether to normalize file names (remove accents), default false
 * @param options.query Optional query string to add (for customer files API)
 * @returns FormData instance with files appended
 */
export function createFilesFormData(
	files: File | File[],
	options?: {
		normalizeFileName?: boolean;
		query?: string;
	},
): FormData {
	const formData = new FormData();
	const filesArray = Array.isArray(files) ? files : [files];
	const shouldNormalize = options?.normalizeFileName ?? false;

	for (const file of filesArray) {
		const fileName = shouldNormalize ? normalizeFileName(file.name) : file.name;
		formData.append("files", file, fileName);
	}

	if (options?.query) {
		formData.append("q", options.query);
	}

	return formData;
}

/**
 * Creates FormData for a single named file field
 * @param fieldName Form field name expected by backend (e.g. "rib", "file")
 * @param file File to upload
 * @param options Optional configuration
 * @param options.normalizeFileName Whether to normalize file name (remove accents), default false
 */
export function createNamedFileFormData(
	fieldName: string,
	file: File,
	options?: {
		normalizeFileName?: boolean;
	},
): FormData {
	const formData = new FormData();
	const shouldNormalize = options?.normalizeFileName ?? false;
	const fileName = shouldNormalize ? normalizeFileName(file.name) : file.name;
	formData.append(fieldName, file, fileName);
	return formData;
}

/**
 * Creates FormData for multiple files under the same field name
 * @param fieldName Form field name expected by backend (e.g. "rib", "files")
 * @param files Files to upload
 * @param options Optional configuration
 * @param options.normalizeFileName Whether to normalize file names (remove accents), default false
 */
export function createNamedFilesFormData(
	fieldName: string,
	files: File[],
	options?: {
		normalizeFileName?: boolean;
	},
): FormData {
	const formData = new FormData();
	const shouldNormalize = options?.normalizeFileName ?? false;
	for (const file of files) {
		const fileName = shouldNormalize ? normalizeFileName(file.name) : file.name;
		formData.append(fieldName, file, fileName);
	}
	return formData;
}

/**
 * Creates PUT FormData request
 */
export async function createPutFormDataRequest<T>(url: URL, body: FormData): Promise<T> {
	const headers = buildFormDataHeaders(url.pathname);

	const request = async (): Promise<Response> => {
		return fetchWithNetworkLogging(
			url.toString(),
			{
				method: "PUT",
				headers,
				body,
				credentials: "include",
			},
			{ timeoutProfile: "upload" },
		);
	};

	return sendRequest<T>(request);
}
